// LicencesInstitutesTable.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import Hoot from "../Hoot";
import RegisterAndUpdateLicenceModal from "./Modals/RegisterAndUpdateLicenceModal";
import "bootstrap/dist/css/bootstrap.min.css";
import getLogo from "../getLogo";

const ServiceTable = [
  { title: "Demonstração", color: "secondary" },
  { title: "Plano básico", color: "success" },
  { title: "Plano premium", color: "primary" },
  { title: "Plano avançado", color: "warning" },
  { title: "Plano platina (universidades)", color: "info" },
  { title: "Plano diamante (universidade)", color: "dark" },
];

const licencesStyles = `
:root{
  --primary:#6052ff;
  --bg:#f4f6fb;
  --card:#ffffff;
  --border:#e2e8f0;
  --text:#0f172a;
  --muted:#6b7280;
}

.lic-shell{
  background:var(--bg);
  padding:8px;
}
.lic-card{
  background:var(--card);
  border-radius:16px;
  border:1px solid var(--border);
  padding:10px 12px 14px;
}

/* header */
.lic-header{
  display:flex;
  flex-wrap:wrap;
  justify-content:space-between;
  align-items:center;
  gap:8px;
  margin-bottom:8px;
}
.lic-header-title{
  font-weight:800;
  font-size:1rem;
  display:flex;
  align-items:center;
  gap:.5rem;
}
.lic-chip{
  display:inline-flex;
  align-items:center;
  gap:.35rem;
  padding:.25rem .6rem;
  border-radius:999px;
  background:#eef2ff;
  color:#4338ca;
  font-size:.78rem;
  font-weight:700;
}

/* toolbar */
.lic-toolbar{
  margin-top:4px;
  padding:8px 10px;
  border-radius:12px;
  background:rgba(148,163,184,0.08);
  margin-bottom:10px;
}

/* tabela + responsivo */
.lic-table-wrapper{
  width:100%;
}
@media (max-width:768px){
  .lic-table-desktop{display:none;}
  .lic-mobile-list{display:block;}
}
@media (min-width:769px){
  .lic-table-desktop{display:block;}
  .lic-mobile-list{display:none;}
}

.lic-table{
  width:100%;
  border-collapse:separate;
  border-spacing:0;
  font-size:.84rem;
  color:var(--text);
}
.lic-table thead th{
  text-align:left;
  padding:.55rem .6rem;
  font-size:.78rem;
  font-weight:700;
  color:var(--muted);
  border-bottom:1px solid var(--border);
  background:var(--card);
}
.lic-table tbody td{
  padding:.55rem .6rem;
  border-bottom:1px solid var(--border);
  vertical-align:middle;
}
.lic-table tbody tr:hover{
  background:rgba(148,163,184,0.06);
}

/* logo */
.lic-logo{
  width:46px;
  height:46px;
  border-radius:10px;
  overflow:hidden;
  background:#e5e7eb;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:.8rem;
  font-weight:700;
  color:#4b5563;
}
.lic-logo img{
  width:100%;
  height:100%;
  object-fit:cover;
}

/* badge plano */
.lic-plan-badge{
  font-size:11px;
  border-radius:999px;
  padding:2px 8px;
  font-weight:600;
}

/* dias restantes badge */
.lic-exp-badge{
  font-size:11px;
  border-radius:999px;
  padding:2px 8px;
  font-weight:700;
}

/* mobile cards */
.lic-mobile-list{
  margin-top:4px;
}
.mlic-card{
  background:var(--card);
  border-radius:14px;
  border:1px solid var(--border);
  padding:10px 12px;
  margin:6px 0;
}
.mlic-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
}
.mlic-line{height:1px;background:var(--border);margin:8px 0;}
.mlic-pill{
  display:inline-flex;
  align-items:center;
  gap:4px;
  border-radius:999px;
  background:rgba(148,163,184,0.15);
  padding:2px 8px;
  font-size:.74rem;
  font-weight:600;
}
.small-muted{
  font-size:.76rem;
  color:var(--muted);
}

/* footer paginacao */
.lic-footer{
  margin-top:8px;
  display:flex;
  justify-content:center;
}
`;

// util
const normalize = (str) =>
  str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// buckets (mesma lógica do analytics)
function toDaysBucket(days) {
  if (days <= 0) return "Expirada";
  if (days < 30) return "< 30";
  if (days < 70) return "30–69";
  return ">= 70";
}

// Plano: usa ServiceTable (0..5)
const renderLicenseBadge = (type) => {
  const t = Number(type);
  const item = Number.isFinite(t) ? ServiceTable[t] : null;

  const title = item?.title ?? "Sem plano";
  // fallback de cores simples (bootstrap)
  const cls = item?.color ? `bg-${item.color}` : "bg-secondary";

  return <span className={`badge lic-plan-badge ${cls}`}>{title}</span>;
};

// badge de expiração: bucket + dias
const getExpiryBadge = (remainingDays) => {
  const days = safeNum(remainingDays);

  const bucket = toDaysBucket(days);

  // cores coerentes
  let cls = "bg-primary"; // >=70
  if (bucket === "Expirada") cls = "bg-danger";
  else if (bucket === "< 30") cls = "bg-danger";
  else if (bucket === "30–69") cls = "bg-warning";
  else if (bucket === ">= 70") cls = "bg-success";

  const label = bucket === "Expirada" ? "Expirada" : `${bucket} dias`;

  return (
    <span className={`badge lic-exp-badge ${cls}`}>
      {label} • {days}d
    </span>
  );
};

const LicencesInstitutesTable = () => {
  const [licences, setLicences] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadData = async () => {
    try {
      const res = await axios.get(Hoot() + "licences/list-all", {
        withCredentials: true,
      });
      setLicences(res.data || []);
    } catch (error) {
      console.clear();
      console.log(error);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredLicences = licences.filter((item) =>
    normalize(item.institute?.name || "").includes(normalize(search))
  );

  const pageCount = Math.ceil(filteredLicences.length / itemsPerPage) || 1;
  const paginatedLicences = filteredLicences.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePaymentClick = (lic) => {
    console.log("Pagar para:", lic.institute?.name);
  };

  // paginação compacta
  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 7;

    if (pageCount <= maxButtons) {
      for (let i = 1; i <= pageCount; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(pageCount - 1, currentPage + 1);

      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < pageCount - 1) pages.push("...");
      pages.push(pageCount);
    }

    return pages.map((p, idx) =>
      p === "..." ? (
        <li key={`ellipsis-${idx}`} className="page-item disabled">
          <span className="page-link">…</span>
        </li>
      ) : (
        <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
          <button className="page-link" onClick={() => setCurrentPage(p)}>
            {p}
          </button>
        </li>
      )
    );
  };

  return (
    <>
      <style>{licencesStyles}</style>
      <div className="lic-shell">
        <div className="lic-card">
          {/* HEADER */}
          <div className="lic-header">
            <div className="lic-header-title">
              <span>Licenças de Instituições</span>
              <span className="lic-chip">
                <i className="bi bi-shield-lock" />
                {filteredLicences.length} registadas
              </span>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <RegisterAndUpdateLicenceModal
                toggle_btn={
                  <button className="btn btn-outline-primary btn-sm rounded-pill">
                    <i className="bi bi-credit-card me-1" />
                    Pagar licença
                  </button>
                }
              />
              <button
                className="btn btn-outline-light btn-sm rounded-pill d-flex align-items-center"
                onClick={loadData}
              >
                <i className="bi bi-arrow-clockwise me-1" />
                Recarregar
              </button>
            </div>
          </div>

          {/* TOOLBAR */}
          <div className="lic-toolbar">
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <input
                  type="search"
                  className="form-control form-control-sm rounded-pill"
                  placeholder="Pesquisar por nome da escola…"
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="col-6 col-md-3 d-flex align-items-center">
                <small className="small-muted">
                  Mostrando {paginatedLicences.length} de {filteredLicences.length}
                </small>
              </div>

              {/* removido: "Página X de Y" */}
              <div className="col-6 col-md-3 d-flex align-items-center justify-content-md-end">
                <small className="small-muted"></small>
              </div>
            </div>
          </div>

          {/* TABELA + MOBILE */}
          <div className="lic-table-wrapper">
            {/* Desktop */}
            <div className="lic-table-desktop">
              <table className="lic-table">
                <thead>
                  <tr>
                    <th>Escola</th>
                    <th>NIF</th>
                    <th>Plano</th>
                    <th>Expira em</th>
                    <th>Dias restantes</th>
                    <th className="text-end">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLicences.map((lic) => {
                    const inst = lic.institute || {};
                    return (
                      <tr key={lic.licence_id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="lic-logo">
                              <img
                                src={getLogo(inst)}
                                alt={(inst && inst.name) || "School logo"}
                                onError={(e) => {
                                  e.currentTarget.src =
                                    "https://shop.raceya.fit/wp-content/uploads/2020/11/logo-placeholder.jpg";
                                }}
                              />
                            </div>
                            <div>
                              <div style={{ fontWeight: 600 }}>{inst.name || "Sem nome"}</div>
                              <div className="small-muted">Código: {inst.code || "—"}</div>
                            </div>
                          </div>
                        </td>

                        <td>{inst.nif || "N/A"}</td>
                        <td>{renderLicenseBadge(lic.type)}</td>

                        <td>
                          <span style={{ fontSize: ".82rem" }}>{lic.expiration || "—"}</span>
                        </td>

                        <td>{getExpiryBadge(lic.remaining_days)}</td>

                        <td className="text-end">
                          <RegisterAndUpdateLicenceModal
                            code={inst.code}
                            toggle_btn={
                              <button
                                className="btn btn-outline-success btn-sm rounded-pill d-flex align-items-center"
                                type="button"
                                onClick={() => handlePaymentClick(lic)}
                              >
                                <i className="bi bi-credit-card me-1" />
                                Pagar
                              </button>
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}

                  {paginatedLicences.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <span className="small-muted">Nenhuma licença encontrada para este filtro.</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lic-mobile-list">
              {paginatedLicences.map((lic) => {
                const inst = lic.institute || {};
                return (
                  <div className="mlic-card" key={lic.licence_id}>
                    <div className="mlic-row">
                      <div className="d-flex align-items-center gap-2">
                        <div className="lic-logo">
                          <img
                            src={getLogo(inst)}
                            alt={(inst && inst.name) || "School logo"}
                            onError={(e) => {
                              e.currentTarget.src =
                                "https://shop.raceya.fit/wp-content/uploads/2020/11/logo-placeholder.jpg";
                            }}
                          />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{inst.name || "Sem nome"}</div>
                          <div className="small-muted">NIF: {inst.nif || "N/A"}</div>
                        </div>
                      </div>
                      <div>{renderLicenseBadge(lic.type)}</div>
                    </div>

                    <div className="mlic-line" />

                    <div className="mlic-row mb-1">
                      <span className="mlic-pill">
                        <i className="bi bi-calendar2-check me-1" />
                        {lic.expiration || "—"}
                      </span>
                      <span>{getExpiryBadge(lic.remaining_days)}</span>
                    </div>

                    <div className="mlic-row">
                      <RegisterAndUpdateLicenceModal
                        code={inst.code}
                        toggle_btn={
                          <button
                            className="btn btn-outline-success btn-sm rounded-pill d-flex align-items-center"
                            type="button"
                            onClick={() => handlePaymentClick(lic)}
                          >
                            <i className="bi bi-credit-card me-1" />
                            Pagar licença
                          </button>
                        }
                      />
                      <span className="small-muted">Código: {inst.code || "—"}</span>
                    </div>
                  </div>
                );
              })}

              {paginatedLicences.length === 0 && (
                <span className="small-muted">Nenhuma licença encontrada para este filtro.</span>
              )}
            </div>
          </div>

          {/* PAGINAÇÃO */}
          <div className="lic-footer">
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                    «
                  </button>
                </li>

                {renderPageNumbers()}

                <li className={`page-item ${currentPage === pageCount ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}>
                    »
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default LicencesInstitutesTable;
